(ns hai.tasks.facets
  "The kanban tasks-board filter surfaces: the assignee facet sidebar - the one
   place the board's filter is set - and the applied-filter chips shown in the
   page header. Both drive the collection's standard `:table/filters` form (the
   sub-field `set-value!` for the sidebar, the map-level field for the chips), so
   the query plumbing is untouched. Options and their counts come from
   `:user/task-facets` via [[hai.tasks.data/load-facets]].

   A task-specific fork of `hai.notes.facets`, deliberately narrowed to a single
   assignee group (the board filters on assignee alone)."
  (:require
   [hai.tasks.data :as data]
   [hai.ui.components.collection.filters :as filters]
   [hai.ui.icons :as icons]
   [hai.ui.rf-sugar :refer [<s >e]]
   [hai.ui.tokens :as t]
   [lambdaisland.ornament :as o]
   [reagent.core :as r]))

(def ^:private person-icon (icons/mui-icon :outlined :person))

;; The filter attr the assignee facet writes. A `:user/uuid` selects that
;; assignee; the `:unassigned` sentinel keyword selects tasks with no assignee.
(def ^:private assigned-to-attr :task/assigned-to)

;; ---------------------------------------------------------------------------
;; Applied-filter chips (page header)
;; ---------------------------------------------------------------------------

(o/defstyled chips-row :div
  {:display "flex" :align-items "center" :flex-wrap "wrap"
   :gap "6px" :min-width "0"})

(o/defstyled filter-chip :span
  {:display "inline-flex" :align-items "center" :gap "6px"
   :height "26px" :padding "0 4px 0 10px"
   :border-radius "999px"
   :border-width "1px" :border-style "solid" :border-color t/--card-forest-14
   :background t/--colour-snow
   :font-size "12px" :color t/--card-forest
   :white-space "nowrap"}
  [:b {:font-weight "600"}]
  [:.lead-icon {:display "inline-flex"}
   [:svg {:width "13px" :height "13px" :fill "currentColor"}]]
  [:.remove
   {:display "inline-flex" :align-items "center" :justify-content "center"
    :width "18px" :height "18px" :border "none" :border-radius "999px"
    :background "transparent" :padding "0"
    :color t/--card-forest :cursor "pointer"}
   [:&:hover {:background t/--card-forest-10}]
   [:svg {:width "11px" :height "11px" :fill "currentColor"}]])

(defn applied-chips
  "The header's row of currently-applied filters - one chip per filter column
   holding a value, showing the column's pill face (`:label`/`:icon`/`:summary`)
   with an × that resets it. Clearing sets the field on the filters SUB-form
   (id `[<root-form-id> :table/filters]`) - the same path the sidebar writes -
   because sub-form values propagate up to the root form but a root-level write
   never reaches the sub-form's fields, which would leave the sidebar's
   selection highlight stale. Renders nothing when no filter is applied."
  [table]
  (let [columns (<s [::filters/columns table])
        filters-form-id [(-> table :table/form :form/id) :table/filters]
        {:form.field/keys [current-value]}
        (get-in table [:table/form :form/fields :table/filters])
        applied (for [{:table.column/keys [attr filter] :as column} @columns
                      :let [{:table.column.filter/keys [label icon summary]} filter
                            value (get current-value attr)]
                      :when (and label (some? value))]
                  ^{:key attr}
                  [filter-chip
                   (when icon [:span.lead-icon [icon]])
                   [:b (str label ":")]
                   [:span (when summary (summary value column))]
                   [:button.remove
                    {:type "button"
                     :aria-label (str "Reset " label " filter")
                     :on-click (fn []
                                 (>e [:form.field/set-value! filters-form-id attr nil])
                                 (>e [::data/load-facets]))}
                    [icons/outlined-close]]])]
    (when (seq applied)
      (into [chips-row] applied))))

;; ---------------------------------------------------------------------------
;; Facet sidebar
;; ---------------------------------------------------------------------------

(o/defstyled sidebar :aside
  {:width "240px"
   :position "sticky" :top "16px"
   :align-self "start"
   :max-height "calc(100vh - 120px)"
   :overflow-y "auto"
   :display "flex" :flex-direction "column" :gap "16px"
   :padding "14px 12px"
   :border-width "1px" :border-style "solid" :border-color t/--card-forest-14
   :border-radius "10px"
   :background t/--colour-snow
   :scrollbar-width "thin"
   :scrollbar-color "rgba(45,62,42,0.4) transparent"}
  [:.facet-group {:display "flex" :flex-direction "column" :gap "6px"}]
  [:.facet-heading
   {:display "flex" :align-items "center" :gap "6px"
    :font-size "11px" :font-weight "700"
    :letter-spacing "0.06em" :text-transform "uppercase"
    :color t/--card-forest-70}
   [:svg {:width "13px" :height "13px" :fill "currentColor"}]]
  [:.facet-options {:display "flex" :flex-direction "column" :gap "2px"}]
  [:.facet-option
   {:display "flex" :align-items "center" :gap "8px"
    :width "100%" :text-align "left"
    :padding "5px 8px" :border "none" :border-radius "7px"
    :background "transparent"
    :font-family "inherit" :font-size "13px" :font-weight "500"
    :color t/--card-forest :cursor "pointer"
    :min-width "0"}
   [:&:hover {:background t/--card-forest-10}]
   [:&.selected {:background t/--card-forest-10 :font-weight "700"}]
   [:&.zero {:opacity "0.45"}]
   [:.opt-label
    {:flex "1" :min-width "0"
     :overflow "hidden" :text-overflow "ellipsis" :white-space "nowrap"}]
   [:.opt-count {:flex "0 0 auto" :font-size "12px" :font-weight "500"
                 :color t/--card-forest-70}]]
  [:.clear-all
   {:align-self "flex-start"
    :border "none" :background "transparent" :padding "0 8px"
    :font-family "inherit" :font-size "12px" :font-weight "600"
    :color t/--card-forest-70 :cursor "pointer"}
   [:&:hover {:color t/--card-forest :text-decoration "underline"}]]
  [:at-media {:max-width "768px"}
   {:position "static" :width "100%" :max-height "none"}])

(defn- facet-option
  [{:keys [label amount selected? on-toggle title]}]
  [:button.facet-option
   {:type     "button"
    :class    (cond-> []
                selected? (conj "selected")
                ;; Nothing matches this option under the current constraints.
                (and (some? amount) (zero? amount) (not selected?)) (conj "zero"))
    :title    (or title label)
    :on-click on-toggle}
   [:span.opt-label label]
   (when (some? amount) [:span.opt-count amount])])

(defn- toggle-value!
  "Single-select facet click: picking the selected option again clears it.
   The counts re-query after - the set event is processed first, so the
   reload sees the new value."
  [set-value! current-value value]
  (set-value! (if (= current-value value) nil value))
  (>e [::data/load-facets]))

(defn- field-state [table attr]
  (get-in table [:table/form :form/fields attr]))

(defn- clear-all!
  [table]
  (doseq [[_ {:form.field/keys [set-value! current-value]}]
          (-> table :table/form :form/fields)]
    (when (and set-value! (some? current-value))
      (set-value! nil)))
  (>e [::data/load-facets]))

(defn- assignees-group
  "The single assignee facet group: an Unassigned sentinel row over every
   distinct assignee in the board's open tasks, each with its smart count.
   Single-select against the `:task/assigned-to` filter field."
  [table assignees unassigned-count]
  (let [{:form.field/keys [current-value set-value!]} (field-state table assigned-to-attr)]
    [:section.facet-group
     [:div.facet-heading [person-icon] "Assignee"]
     (into [:div.facet-options
            [facet-option {:label     "Unassigned"
                           :amount    unassigned-count
                           :selected? (= current-value :unassigned)
                           :on-toggle #(toggle-value! set-value! current-value :unassigned)}]]
           (for [{uuid :user/uuid full-name :user/full-name amount :facet/count} assignees]
             ^{:key uuid}
             [facet-option {:label     full-name
                            :amount    amount
                            :selected? (= current-value uuid)
                            :on-toggle #(toggle-value! set-value! current-value uuid)}]))]))

(defn- sidebar-panel [table]
  (r/with-let [_ (>e [::data/load-facets])]
    (let [{:facet/keys [assignees unassigned-count]} @(<s [::data/facets])
          any-applied? (some (fn [[_ {:form.field/keys [current-value]}]]
                               (some? current-value))
                             (-> table :table/form :form/fields))]
      [sidebar
       [assignees-group table assignees unassigned-count]
       (when any-applied?
         [:button.clear-all
          {:type "button" :on-click #(clear-all! table)}
          "Clear all filters"])])))

(defn panel
  "`:filters/render` for the tasks board: the assignee facet sidebar. The kanban
   view mounts it as a sibling of the lane grid (via `:filters/placement
   :sibling`); the page grid places it in the left column."
  [table]
  [sidebar-panel table])
